import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";

interface TalkingAvatarWordLimitModalProps {
  open: boolean;
  onClose: () => void;
  /** The cap the user hit (e.g. 60 for Free). Used in the body copy. */
  currentLimit: number;
  /** The cap the user could unlock by upgrading. Hidden if they're already
   *  on the highest tier (in which case we just show the cap explanation). */
  upgradeLimit?: number;
}

export function TalkingAvatarWordLimitModal({
  open,
  onClose,
  currentLimit,
  upgradeLimit,
}: TalkingAvatarWordLimitModalProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canUpgrade = !!upgradeLimit && upgradeLimit > currentLimit;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t.talkingAvatar.wordLimitModalTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {canUpgrade
            ? t.talkingAvatar.wordLimitModalBodyUpgrade
                .replace("{current}", String(currentLimit))
                .replace("{upgrade}", String(upgradeLimit))
            : t.talkingAvatar.wordLimitModalBodyHardCap.replace("{current}", String(currentLimit))}
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-border/50 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-border bg-card hover:bg-secondary text-sm font-medium text-foreground"
          >
            {t.talkingAvatar.wordLimitModalCloseCta}
          </button>
          {canUpgrade && (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/pricing");
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm font-bold"
            >
              <Sparkles className="w-4 h-4" />
              {t.talkingAvatar.wordLimitModalUpgradeCta}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
